import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useUserRole } from "@/hooks/use-user-role";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AvatarUpload } from "@/components/AvatarUpload";
import { SingingPreferencesForm } from "@/components/SingingPreferencesForm";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, Save, User, Menu, Music } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import logo from "@/assets/logo.svg";

const Profile = () => {
  const navigate = useNavigate();
  const { role, isAdmin, isCoach, loading: roleLoading } = useUserRole();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [profile, setProfile] = useState({
    id: "",
    full_name: "",
    email: "",
    avatar_url: "",
    singing_genres: [] as string[],
    singing_level: "",
    singing_goals: ""
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate("/auth");
        return;
      }

      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", user.id)
        .single();

      if (error) throw error;

      setProfile({
        id: user.id,
        full_name: data?.full_name || "",
        email: data?.email || user.email || "",
        avatar_url: data?.avatar_url || "",
        singing_genres: data?.singing_genres || [],
        singing_level: data?.singing_level || "",
        singing_goals: data?.singing_goals || ""
      });
    } catch (error) {
      console.error("Error fetching profile:", error);
      toast({
        title: "Error",
        description: "Failed to load profile",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const { error } = await supabase
        .from("profiles")
        .update({
          full_name: profile.full_name
        })
        .eq("id", profile.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Profile updated successfully!"
      });
    } catch (error) {
      console.error("Error updating profile:", error);
      toast({
        title: "Error",
        description: "Failed to update profile",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const getBackPath = () => {
    if (isAdmin) return "/admin";
    if (isCoach) return "/coach";
    return "/booking";
  };

  const getRoleBadgeVariant = () => {
    if (isAdmin) return "destructive";
    if (isCoach) return "default";
    return "secondary";
  };

  if (loading || roleLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-accent/20">
      <header className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2 md:gap-3">
              <img src={logo} alt="Vocalzing logo" className="h-8 w-8 md:h-10 md:w-10" />
              <div>
                <h1 className="text-lg md:text-2xl font-bold">Profile Settings</h1>
                <p className="text-xs md:text-sm text-muted-foreground hidden sm:block">Manage your account</p>
              </div>
            </div>
            
            {/* Desktop Navigation */}
            <Button variant="ghost" onClick={() => navigate(getBackPath())} className="hidden md:flex">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>

            {/* Mobile Hamburger Menu */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild className="md:hidden">
                <Button variant="ghost" size="sm">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px]">
                <div className="flex flex-col gap-4 mt-8">
                  <Button 
                    onClick={() => {
                      navigate(getBackPath());
                      setMobileMenuOpen(false);
                    }} 
                    variant="ghost" 
                    className="w-full justify-start"
                  >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back to Dashboard
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-2xl">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <User className="w-5 h-5" />
                  Personal Information
                </CardTitle>
                <CardDescription>
                  Update your profile information and manage your account
                </CardDescription>
              </div>
              <Badge variant={getRoleBadgeVariant()}>
                {role === "admin" ? "Admin" : role === "coach" ? "Coach" : "Student"}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex justify-center">
              <AvatarUpload
                currentAvatarUrl={profile.avatar_url}
                userId={profile.id}
                onAvatarUpdate={(url) => setProfile({ ...profile, avatar_url: url })}
              />
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={profile.email}
                  disabled
                  className="bg-muted"
                />
                <p className="text-xs text-muted-foreground">
                  Email cannot be changed
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="full_name">Full Name</Label>
                <Input
                  id="full_name"
                  type="text"
                  value={profile.full_name}
                  onChange={(e) => setProfile({ ...profile, full_name: e.target.value })}
                  placeholder="Enter your full name"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => navigate(getBackPath())}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                <Save className="w-4 h-4 mr-2" />
                {saving ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Singing Preferences */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Music className="w-5 h-5" />
              Singing Preferences
            </CardTitle>
            <CardDescription>
              Your musical interests and goals
            </CardDescription>
          </CardHeader>
          <CardContent>
            <SingingPreferencesForm
              userId={profile.id}
              existingPreferences={{
                singing_genres: profile.singing_genres,
                singing_level: profile.singing_level,
                singing_goals: profile.singing_goals
              }}
              onComplete={() => {
                toast({
                  title: "Success",
                  description: "Preferences updated successfully!"
                });
                fetchProfile();
              }}
            />
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default Profile;
